class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.backdrop = this.querySelector('.cart-notification-backdrop');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    // The header cart icon now opens this same panel directly instead
    // of navigating to /cart (per explicit request) — see
    // setHeaderCartIconTrigger() below, called at the end of this
    // constructor. product_id is never set from this path (there's no
    // "just added" product when opening from the header), so
    // lastAddedProductId simply stays whatever it already was —
    // refreshRecommendations() already no-ops without a product id.
    this.lastAddedProductId = null;

    // Swipe-to-remove state — see handleSwipeStart/Move/End below.
    // this.openRow tracks whichever single row (a .cart-notification-item-row
    // element) is currently dragged open, revealing its red remove
    // button, so a second row can never be left open at the same time
    // and so an outside tap knows what to close.
    this.swipeState = null;
    this.openRow = null;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // Scoped to the actual close-triggering elements (not a generic
    // button[type="button"] selector): cart-notification-product is
    // server-rendered with whatever's already in the cart on every page
    // load, so if the cart already has items when this constructor runs,
    // a generic selector would also match the quantity +/- buttons below
    // and bind close() directly to them — clicking + would then both
    // increment (via the delegated listener) and instantly close the
    // notification, which looked like the item had been removed.
    this.querySelectorAll('.cart-notification__close, .cart-notification__backdrop').forEach((closeTrigger) =>
      closeTrigger.addEventListener('click', this.close.bind(this))
    );

    // Delegated (not attached directly to the buttons) because
    // cart-notification-product's innerHTML — and the quantity/remove
    // controls inside it — gets replaced on every cart change; a direct
    // listener wouldn't survive that swap, but one on <cart-notification>
    // itself (never replaced) does, for controls added now or later alike.
    this.addEventListener('click', (evt) => {
      // If a row is currently swiped open, any tap other than on its
      // own reveal button just closes it and swallows the click —
      // the same "tap elsewhere cancels, doesn't also activate
      // whatever was under your finger" behavior iOS's own
      // swipe-to-delete uses. A tap that lands ON the reveal button
      // is deliberately exempt (falls through below) so opening a row
      // and immediately tapping remove works in one motion.
      if (this.openRow && !evt.target.closest('.cart-notification-item__reveal-btn')) {
        this.closeOpenSwipeRow();
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }

      const qtyButton = evt.target.closest('.cart-notification-item__qty-btn');
      if (qtyButton) {
        evt.preventDefault();
        const valueEl = qtyButton
          .closest('.cart-notification-item__quantity')
          .querySelector('.cart-notification-item__qty-value');
        const currentQuantity = parseInt(valueEl.textContent, 10) || 0;
        const nextQuantity = qtyButton.dataset.direction === 'plus' ? currentQuantity + 1 : currentQuantity - 1;
        this.handleQuantityChange(qtyButton.dataset.lineKey, nextQuantity);
        return;
      }

      // Small trash-can button pinned to the row's top-right corner.
      const removeButton = evt.target.closest('.cart-notification-item__remove');
      if (removeButton) {
        evt.preventDefault();
        this.removeItem(removeButton.dataset.lineKey);
        return;
      }

      // The red button revealed by swiping a row left.
      const revealButton = evt.target.closest('.cart-notification-item__reveal-btn');
      if (revealButton) {
        evt.preventDefault();
        this.openRow = null;
        this.removeItem(revealButton.dataset.lineKey);
      }
      // Discount-code apply/remove is handled entirely by the shared
      // <cart-discount-form> custom element (assets/cart-discount.js)
      // itself now — also used, unmodified, by the full cart page — so
      // there's nothing to delegate here for it anymore.
    });

    // Swipe-to-remove — Pointer Events (not touch/mouse separately),
    // delegated on <cart-notification> itself for the same reason the
    // click handler above is: cart-notification-product's innerHTML is
    // replaced wholesale on every cart change, so listeners bound
    // directly to a row wouldn't survive that swap.
    this.addEventListener('pointerdown', this.handleSwipeStart.bind(this));
    this.addEventListener('pointermove', this.handleSwipeMove.bind(this), { passive: false });
    this.addEventListener('pointerup', this.handleSwipeEnd.bind(this));
    this.addEventListener('pointercancel', this.handleSwipeEnd.bind(this));

    this.setHeaderCartIconTrigger();
  }

  // Header cart icon → opens this panel directly instead of
  // navigating to /cart, mirroring exactly how the theme's own
  // (dormant, since settings.cart_type isn't "drawer" on this theme)
  // CartDrawer class wires up the same #cart-icon-bubble link in
  // assets/cart-drawer.js's setHeaderCartIconAccessibility() — same
  // preventDefault + open() pattern, same role/aria-haspopup/keydown
  // accessibility treatment, just targeting this panel instead.
  setHeaderCartIconTrigger() {
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (!cartIcon) return;

    cartIcon.setAttribute('role', 'button');
    cartIcon.setAttribute('aria-haspopup', 'dialog');
    cartIcon.addEventListener('click', (evt) => {
      evt.preventDefault();
      this.refreshAndOpen();
    });
    cartIcon.addEventListener('keydown', (evt) => {
      if (evt.code === 'Space') {
        evt.preventDefault();
        this.refreshAndOpen();
      }
    });
  }

  // Re-fetches the same sections a normal add-to-cart already keeps
  // in sync (getSectionsToRender()) before opening, so the panel
  // reflects the CURRENT cart even if it changed since this page last
  // loaded (a different tab, another device, etc.) rather than
  // relying solely on whatever was server-rendered at page-load time.
  // {{path}}?sections=id1,id2 is Shopify's standard read-only
  // section-rendering endpoint — works on any URL, including the
  // current page, and never mutates the cart. Opens regardless of
  // whether the fetch succeeds, so a network hiccup never makes the
  // icon feel unresponsive — worst case it just shows the
  // already-rendered (slightly stale) content instead of nothing.
  refreshAndOpen() {
    const sectionIds = this.getSectionsToRender().map((section) => section.id);
    Promise.all([
      fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`).then((response) => response.json()),
      // getSectionsToRender() never includes cart-notification-recommendations
      // (see refreshRecommendations's own comment) — a plain /cart.js
      // fetch is the only way to know if the CURRENT cart (which this
      // whole method exists to reflect, per its own doc comment above)
      // is actually empty, so a stale "Complete Your Look" from before
      // the cart was emptied gets cleared rather than left showing.
      fetch(`${routes.cart_url}.js`).then((response) => response.json()),
    ])
      .then(([sections, cart]) => {
        sectionIds.forEach((id) => {
          if (sections[id]) this.renderSection(id, sections[id]);
        });
        if (cart.item_count === 0) this.clearRecommendations();
        else this.ensureRecommendations(cart);
      })
      .catch((error) => console.error(error))
      .finally(() => this.open());
  }

  // Cart icon (Kiana, 10 Oct 2026): « Complète ton look » also shows when the cart is opened from the header,
  // not only right after an add (on a new page it was left empty). Keyed off the newest item in the cart, the
  // same piece the section's pairings start from (sections/cart-notification-recommendations.liquid). Reloaded
  // only when the cart changed since it was last loaded, so opening the cart again doesn't make it flicker.
  ensureRecommendations(cart) {
    const first = cart.items && cart.items[0];
    if (!first) return;
    const sig = cart.items.map((item) => `${item.key}:${item.quantity}`).join('|');
    const current = document.getElementById('cart-notification-recommendations');
    const loaded = current && current.tagName === 'PRODUCT-RECOMMENDATIONS' && current.innerHTML.trim() !== '';
    if (loaded && this.recsCartSig === 'after-add') {
      this.recsCartSig = sig;
      return;
    }
    if (loaded && sig === this.recsCartSig) return;
    this.recsCartSig = sig;
    this.lastAddedProductId = null; // so refreshRecommendations builds a fresh grid even for the same product
    this.refreshRecommendations(first.product_id);
  }

  // Resets #cart-notification-recommendations back to its original
  // empty-placeholder shape (see snippets/cart-notification.liquid) and
  // hides its wrap (see setRecommendationsWrapHidden) — this is what
  // makes "Complete Your Look" disappear once the cart is empty instead
  // of continuing to show whatever was last added. Also resets
  // lastAddedProductId so the NEXT add-to-cart (a possibly-repeated
  // product) is treated as new and correctly re-fetches recommendations
  // rather than being skipped as "unchanged since last time".
  clearRecommendations() {
    const current = document.getElementById('cart-notification-recommendations');
    if (!current) return;
    const fresh = document.createElement('div');
    fresh.id = 'cart-notification-recommendations';
    fresh.className = 'cart-notification-recommendations';
    current.replaceWith(fresh);
    this.lastAddedProductId = null;
    this.setRecommendationsWrapHidden(true);
  }

  // The theme's own --header-height custom property (set once by
  // <sticky-header>'s connectedCallback, see sections/header.liquid)
  // can go stale — it isn't re-measured on every load, so if the
  // header's real rendered height ever differs from whatever it was
  // when that ran, the drawer (which positions itself under the
  // header using that variable) opens with a visible gap or overlap.
  // Rather than touch header.liquid (shared, out of Cart Drawer
  // scope), measure the header's ACTUAL current height ourselves
  // right before opening and expose it as our own drawer-scoped
  // variable, which diversity-accents.css's mobile positioning rule
  // prefers over the shared one — always accurate, self-contained.
  syncHeaderHeight() {
    const header = document.querySelector('.section-header');
    if (header) {
      this.notification.style.setProperty('--cart-notification-header-height', `${header.getBoundingClientRect().height}px`);
    }
  }

  open() {
    this.syncHeaderHeight();
    this.notification.classList.add('animate', 'active');
    if (this.backdrop) this.backdrop.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    if (this.backdrop) this.backdrop.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      this.renderSection(section.id, parsedState.sections[section.id]);
    });

    // parsedState here is the raw /cart/add.js response for the item that
    // was just successfully added — product_id is a top-level field on
    // it. Only refresh recommendations when it's actually a different
    // product than last time, so bumping the quantity of something
    // already in the cart doesn't keep re-fetching the same grid.
    if (parsedState.product_id) this.refreshRecommendations(parsedState.product_id);
    this.recsCartSig = 'after-add'; // the grid was just built for this cart: the next open from the icon keeps it

    if (this.header) this.header.reveal();
    this.open();
  }

  // Single place that knows how to write a fetched section's HTML into
  // its container — routes the shipping bar through the width-animation
  // helper (see updateShippingBar) instead of a plain innerHTML swap,
  // every other section keeps the original behavior. Shared by
  // renderContents (fresh add-to-cart) and changeLineQuantity (qty/
  // remove) below since both need the exact same swap logic.
  renderSection(sectionId, html) {
    if (sectionId === 'cart-notification-shipping-bar') {
      this.updateShippingBar(html);
      return;
    }
    const target = document.getElementById(sectionId);
    if (target) target.innerHTML = this.getSectionInnerHTML(html);
  }

  // Fixes the shipping bar snapping straight to its new width instead of
  // animating: the fill's width is baked into freshly-fetched HTML as an
  // inline style, and a browser doesn't animate an element's very first
  // style application, even with a CSS transition defined on it (there's
  // no "previous width" for that transition to start from) — this is why
  // the bar always jumped straight to the end position even though
  // diversity-accents.css already has `transition: width 0.4s ease` on
  // .cart-shipping-progress__fill.
  //
  // Fix: read the OLD fill's current width before swapping (0 if there
  // wasn't one — e.g. first item into an empty cart), insert the new
  // markup with its fill forced back to that OLD width, force a reflow
  // so the browser registers that as the starting point, then set it to
  // the real target width (read from data-progress, the server-computed
  // percent — see snippets/cart-shipping-progress.liquid) on the next
  // frame. That's a genuine width CHANGE on an already-painted element,
  // so the existing CSS transition animates it for real. One code path
  // handles every case in the spec (0→X, X→Y, backward on removal, up to
  // 100%) since it's just "old value vs new value," nothing scenario-
  // specific.
  updateShippingBar(html) {
    const container = document.getElementById('cart-notification-shipping-bar');
    if (!container) return;

    const oldFill = container.querySelector('.cart-shipping-progress__fill');
    const oldPercent = oldFill ? parseFloat(oldFill.style.width) || 0 : 0;

    container.innerHTML = this.getSectionInnerHTML(html);

    const newFill = container.querySelector('.cart-shipping-progress__fill');
    if (!newFill) return;
    const targetPercent = parseFloat(newFill.dataset.progress);
    if (isNaN(targetPercent)) return;

    newFill.style.width = oldPercent + '%';
    void newFill.offsetWidth; // force reflow: register the old width before changing it again
    requestAnimationFrame(() => {
      newFill.style.width = targetPercent + '%';
    });
  }

  // "Complete Your Look" is keyed off whichever product was most
  // recently added, using the theme's existing <product-recommendations>
  // element (assets/global.js) completely unmodified — that element only
  // ever fetches once per its own lifetime (its IntersectionObserver
  // unobserves itself after firing), so to show recommendations for a
  // NEW product this builds a fresh instance (new data-product-id, empty
  // content) rather than reimplementing any fetch logic — a fresh
  // element means a fresh connectedCallback, which re-triggers the
  // existing lazy-load behavior exactly as designed. Works the same way
  // whether the current element is still the initial plain placeholder
  // div (see cart-notification.liquid) or an already-loaded
  // <product-recommendations> from a previous add.
  refreshRecommendations(productId) {
    if (!productId || String(productId) === String(this.lastAddedProductId)) return;
    this.lastAddedProductId = productId;

    const current = document.getElementById('cart-notification-recommendations');
    if (!current) return;

    const fresh = document.createElement('product-recommendations');
    fresh.id = 'cart-notification-recommendations';
    fresh.className = 'cart-notification-recommendations';
    fresh.dataset.url = `${this.dataset.recommendationsUrl}?limit=4&intent=related&ds_cart=${Date.now()}`; // depends on the cart: never a stored copy
    fresh.dataset.sectionId = 'cart-notification-recommendations';
    fresh.dataset.productId = productId;
    current.replaceWith(fresh);

    // A real add just happened, so the cart can't be empty — undo
    // whatever clearRecommendations() may have hidden earlier.
    this.setRecommendationsWrapHidden(false);
  }

  setRecommendationsWrapHidden(hidden) {
    const wrap = document.querySelector('.cart-notification-recommendations-wrap');
    if (wrap) wrap.classList.toggle('cart-notification-recommendations-wrap--hidden', hidden);
  }

  // Real /cart/change.js call (the same Shopify cart API the rest of
  // the theme's cart JS uses, e.g. cart-remove-button and quantity-input
  // in global.js/cart.js) — not a separate cart implementation. Shared
  // by removeItem and updateItemQuantity below since both are just this
  // same call with a different target quantity.
  changeLineQuantity(lineKey, quantity) {
    const sections = this.getSectionsToRender();
    const body = JSON.stringify({
      id: lineKey,
      quantity,
      sections: sections.map((section) => section.id),
      sections_url: window.location.pathname,
    });

    return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        // Shopify rejects some quantity changes (most commonly hitting an
        // inventory limit) with { errors: "..." } instead of the normal
        // cart + sections payload -- reading parsedState.sections in that
        // case throws, silently leaving the row stuck on whatever number
        // had been optimistically read before the request went out (this
        // is the theme's own cart.js pattern too: it checks
        // parsedState.errors before ever touching .sections). Resync the
        // displayed quantity from the real cart instead of crashing.
        if (parsedState.errors) {
          console.error(parsedState.errors);
          this.resyncQuantity(lineKey);
          return null;
        }

        sections.forEach((section) => {
          this.renderSection(section.id, parsedState.sections[section.id]);
        });
        this.recsCartSig = null; // quantity changed or item removed: the next open from the icon reloads « Complète ton look »
        return parsedState;
      })
      .catch((error) => console.error(error));
  }

  resyncQuantity(lineKey) {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        const item = cart.items.find((line) => line.key === lineKey);
        const valueEl = document.querySelector(
          `.cart-notification-item-wrapper[data-line-key="${lineKey}"] .cart-notification-item__qty-value`
        );
        if (item && valueEl) valueEl.textContent = item.quantity;
      })
      .catch((error) => console.error(error));
  }

  // cart-notification-product IS re-rendered here (unlike a plain add),
  // since removing one item out of several should update the remaining
  // list, not just disappear; the notification only closes once the
  // cart is actually empty.
  removeItem(lineKey) {
    if (!lineKey) return;
    this.changeLineQuantity(lineKey, 0).then((parsedState) => {
      if (parsedState?.item_count === 0) {
        // Currently invisible (the panel closes right after), but keeps
        // the DOM correct for the next open — otherwise reopening via
        // the header icon without an intervening fresh add would
        // briefly rely on refreshAndOpen's own check alone.
        this.clearRecommendations();
        this.close();
      }
    });
  }

  // Reveal width in real pixels — matches .cart-notification-item__reveal-btn's
  // 9rem width in diversity-accents.css. Read live from the root font
  // size rather than a hardcoded 144 so it stays correct even if the
  // theme's rem base ever changes.
  static REVEAL_WIDTH_REM = 9;

  getRevealWidthPx() {
    return CartNotification.REVEAL_WIDTH_REM * parseFloat(getComputedStyle(document.documentElement).fontSize);
  }

  closeOpenSwipeRow() {
    if (!this.openRow) return;
    const foreground = this.openRow.querySelector('.cart-notification-item');
    if (foreground) foreground.style.transform = '';
    this.openRow = null;
  }

  // Direction is deliberately UNDECIDED for the first ~8px of movement
  // (see handleSwipeMove) — only once a drag is clearly more
  // horizontal than vertical does this ever call preventDefault or
  // touch the row's transform; an ambiguous or vertical drag is left
  // completely alone so the browser's own native vertical scrolling
  // (touch-action: pan-y on .cart-notification-item, see
  // diversity-accents.css) handles it exactly as it would anywhere
  // else in the drawer. This is what keeps "swipe a product row to
  // remove it" and "scroll the drawer by dragging over a product row"
  // from ever fighting each other.
  handleSwipeStart(evt) {
    if (evt.isPrimary === false) return;
    const row = evt.target.closest('.cart-notification-item-row');
    if (!row) return;
    const foreground = row.querySelector('.cart-notification-item');
    if (!foreground) return;

    this.swipeState = {
      row,
      foreground,
      pointerId: evt.pointerId,
      startX: evt.clientX,
      startY: evt.clientY,
      startTranslate: row === this.openRow ? -this.getRevealWidthPx() : 0,
      direction: null,
      lastTranslate: 0,
    };
  }

  handleSwipeMove(evt) {
    const state = this.swipeState;
    if (!state || evt.pointerId !== state.pointerId) return;

    const dx = evt.clientX - state.startX;
    const dy = evt.clientY - state.startY;

    if (state.direction === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      // Requires a clearly horizontal bias (not just barely more X
      // than Y) before committing to the swipe gesture, so an
      // intentional-but-slightly-diagonal vertical scroll still goes
      // to the drawer, not the row.
      state.direction = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'horizontal' : 'vertical';
      if (state.direction === 'horizontal') {
        if (this.openRow && this.openRow !== state.row) this.closeOpenSwipeRow();
        state.foreground.classList.add('is-swiping');
      } else {
        return;
      }
    }

    if (state.direction !== 'horizontal') return;

    evt.preventDefault();
    const revealWidth = this.getRevealWidthPx();
    // Clamped to [-1.6x reveal width, 0]: never drags right past
    // closed, and the extra headroom past the reveal width itself is
    // what a "big/fast swipe" in handleSwipeEnd below measures against
    // to decide "auto-remove" vs. "just open the reveal button."
    const next = Math.min(0, Math.max(-revealWidth * 1.6, state.startTranslate + dx));
    state.lastTranslate = next;

    // Batched via requestAnimationFrame rather than writing the style
    // synchronously on every pointermove: touch fires move events much
    // faster than the display can paint, so writing on every single one
    // does redundant work and can visibly stutter — coalescing to one
    // write per animation frame (always using the LATEST position by
    // the time the frame runs) is what actually tracks the finger
    // smoothly instead of janking.
    if (!state.rafScheduled) {
      state.rafScheduled = true;
      requestAnimationFrame(() => {
        state.rafScheduled = false;
        if (this.swipeState === state) {
          state.foreground.style.transform = `translateX(${state.lastTranslate}px)`;
        }
      });
    }
  }

  handleSwipeEnd(evt) {
    const state = this.swipeState;
    if (!state || evt.pointerId !== state.pointerId) return;
    this.swipeState = null;

    if (state.direction !== 'horizontal') return;
    state.foreground.classList.remove('is-swiping');

    const revealWidth = this.getRevealWidthPx();
    const translate = state.lastTranslate;

    if (translate <= -revealWidth * 1.5) {
      // Swiped well past the reveal button itself — treat as explicit
      // "remove this" intent, no second tap needed.
      state.foreground.style.transform = 'translateX(-100%)';
      if (this.openRow === state.row) this.openRow = null;
      this.removeItem(state.row.dataset.lineKey);
      return;
    }

    if (translate <= -revealWidth / 2) {
      state.foreground.style.transform = `translateX(-${revealWidth}px)`;
      this.openRow = state.row;
    } else {
      state.foreground.style.transform = '';
      if (this.openRow === state.row) this.openRow = null;
    }
  }

  // The quantity shown always comes straight from this fresh response
  // (item.quantity in cart-notification-product.liquid) — never tracked
  // separately client-side.
  updateItemQuantity(lineKey, quantity) {
    if (!lineKey || quantity < 1) return;
    this.changeLineQuantity(lineKey, quantity);
  }

  // Minus is never disabled (there's no trash icon in the stepper
  // itself): pressing it at quantity 1 should remove the line, same as
  // the dedicated trash button does — this just routes that one case to
  // removeItem instead of a no-op.
  handleQuantityChange(lineKey, nextQuantity) {
    if (!lineKey) return;
    if (nextQuantity <= 0) {
      this.removeItem(lineKey);
    } else {
      this.updateItemQuantity(lineKey, nextQuantity);
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
      {
        id: 'cart-notification-shipping-bar',
      },
      {
        id: 'cart-notification-summary',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
